/* ═══════════════════════════════════════════════════════════════
   Encrypted localStorage usando Web Crypto API (AES-GCM)

   - Protege PII (nome, email) contra leitura casual via DevTools,
     extensions maliciosas e XSS (os tokens JWT estão em httpOnly cookies).
   - A chave é derivada de uma passphrase estática via PBKDF2 — não é
     um segredo, mas eleva a barra de proteção significativamente.
   - Fallback para JSON puro se crypto.subtle não estiver disponível
     (ex: HTTP sem localhost em dev).
═══════════════════════════════════════════════════════════════ */

const PASSPHRASE = "EvoraMaxx-2026-localStorage";
const SALT = "evoramaxx-storage-v1";
const IV_LENGTH = 12;

let keyPromise: Promise<CryptoKey> | null = null;

function isSubtleCryptoAvailable(): boolean {
  return typeof crypto !== "undefined" && !!crypto.subtle;
}

function getKey(): Promise<CryptoKey> {
  if (keyPromise) return keyPromise;

  keyPromise = (async () => {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      enc.encode(PASSPHRASE),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    return crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: enc.encode(SALT), iterations: 100_000, hash: "SHA-256" },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  })();

  return keyPromise;
}

function toBase64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/* ═══════════════════════════════════════════════════════════════
   API pública
═══════════════════════════════════════════════════════════════ */

export async function encryptedSetItem<T>(key: string, value: T): Promise<void> {
  if (!isSubtleCryptoAvailable()) {
    localStorage.setItem(key, JSON.stringify(value));
    return;
  }

  try {
    const cryptoKey = await getKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoded = new TextEncoder().encode(JSON.stringify(value));
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      cryptoKey,
      encoded,
    );

    // IV (12 bytes) + ciphertext concatenados em base64
    const combined = new Uint8Array(iv.length + new Uint8Array(ciphertext).length);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);

    localStorage.setItem(key, toBase64(combined.buffer));
  } catch {
    // Fallback: salva em JSON puro se a criptografia falhar
    localStorage.setItem(key, JSON.stringify(value));
  }
}

export async function encryptedGetItem<T>(key: string): Promise<T | null> {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  if (!isSubtleCryptoAvailable()) {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  try {
    const combined = fromBase64(raw);
    const iv = combined.slice(0, IV_LENGTH);
    const ciphertext = combined.slice(IV_LENGTH);

    const cryptoKey = await getKey();
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      cryptoKey,
      ciphertext,
    );

    return JSON.parse(new TextDecoder().decode(decrypted)) as T;
  } catch {
    // Fallback: tenta ler como JSON puro (migração de dados antigos)
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
}

export function encryptedRemoveItem(key: string): void {
  localStorage.removeItem(key);
}
